import ProtectedComponent from "../../../../components/ProtectedComponent";
import NavigationLayout from "../../navigation";
import "./AgentEmployeeDetails.scss";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import BackArrowIcon from "../../../../components/icons/BackArrowIcon";
import { useEffect, useRef, useState } from "react";
import { AgencyEmployeeApi } from "../../../../utils/api/AgencyEmployeeApi";
import { getCdnImageUrl, getImageUrl } from "../../../../utils/helpers/common";
import toast from "react-hot-toast";
import AgencyEmployeeStatusMenu, {
  EmployeeConfirmType,
} from "./AgentEmployeeStatusMenu";
import { clientRoutes } from "../../../../utils/routes";
import { AgentEmployeeApi } from "../../../../utils/api/AgentEmployeeApi";
import AgentEmployeeInfoView from "./AgentEmployeeInfoView";
import AgentEmployeeTabsView from "./AgentEmployeeTabsView";
import { CircularProgress, Modal } from "@mui/material";
import Loading from "../../../loading/Loading";
import CloseIcon from "../../../../components/icons/CloseIcon";

const getEmployeeProfileImageUrl = (uploadRecord: any) => {
  if (!uploadRecord) return "";
  if (uploadRecord.medium_image_url)
    return getCdnImageUrl(uploadRecord.medium_image_url);
  if (uploadRecord.small_image_url)
    return getCdnImageUrl(uploadRecord.small_image_url);
  if (uploadRecord.storage_key) return getCdnImageUrl(uploadRecord.storage_key);
  if (uploadRecord.compressed_file_path)
    return getImageUrl(uploadRecord.compressed_file_path);
  if (uploadRecord.original_file_path)
    return getImageUrl(uploadRecord.original_file_path);
  return "";
};

const getEmployeeLargeImageUrl = (uploadRecord: any) => {
  if (!uploadRecord) return "";
  if (uploadRecord.large_image_url)
    return getCdnImageUrl(uploadRecord.large_image_url);
  if (uploadRecord.medium_image_url)
    return getCdnImageUrl(uploadRecord.medium_image_url);
  if (uploadRecord.storage_key) return getCdnImageUrl(uploadRecord.storage_key);
  if (uploadRecord.original_file_path)
    return getImageUrl(uploadRecord.original_file_path);
  return "";
};

const getConfirmConfig = (type: EmployeeConfirmType) => {
  switch (type) {
    case "activate":
      return {
        title: "Activate employee?",
        lines: [
          "Are you sure you want to reactivate this employee?",
          "Reactivating this account will enable all associated permissions and allow the employee to access their dashboard.",
        ],
        note: "",
        confirmText: "Confirm",
        confirmVariant: "dark" as const,
        nextStatus: "active",
      };
    case "deactivate":
      return {
        title: "Deactivate employee?",
        lines: [
          "Are you sure you want to deactivate this employee?",
          "This employee will no longer be able to log in or access any system resources.",
        ],
        note: "",
        confirmText: "Deactivate",
        confirmVariant: "danger" as const,
        nextStatus: "deactivated",
      };
    case "archive":
      return {
        title: "Archive employee?",
        lines: [
          "Archiving hides this employee from your main view to keep your workspace organized.",
          "You can still access their history and reports later.",
        ],
        note: "The employee will lose all login access and system permissions immediately.",
        confirmText: "Confirm",
        confirmVariant: "dark" as const,
        nextStatus: "archived",
      };
    case "unarchive":
      return {
        title: "Unarchive employee?",
        lines: [
          "This will move the employee back to your main workspace and active list.",
        ],
        note: "Their login access and system permissions will be restored, allowing them to access the platform again.",
        confirmText: "Confirm",
        confirmVariant: "dark" as const,
        nextStatus: "active",
      };
  }
};

const AgentEmployeeDetails = () => {
  const navigate = useNavigate();
  const params: any = useParams();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type") || "info";

  const [state, setState] = useState({
    isLoading: true,
    employee: null as any,
  });

  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(true);
  const imageRef = useRef<HTMLImageElement>(null);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmType, setConfirmType] = useState<EmployeeConfirmType | null>(
    null,
  );
  const [confirmLoading, setConfirmLoading] = useState(false);

  const employeeId = params?.id;

  const fetchEmployee = async () => {
    setState((p) => ({ ...p, isLoading: true }));

    const res = await AgentEmployeeApi.getById(employeeId);

    if (res.success && res.data?.success) {
      setState({ isLoading: false, employee: res.data.data });
    } else {
      setState({ isLoading: false, employee: null });
    }
  };

  useEffect(() => {
    if (!employeeId) return;
    fetchEmployee();
  }, [employeeId]);

  useEffect(() => {
    setImageLoading(true);
    setImageError(false);
    setShowSkeleton(true);

    const employee = state.employee;
    const url = getEmployeeProfileImageUrl(employee?.profile_photo);

    if (url) {
      const img = new Image();
      img.src = url;
      if (img.complete) {
        setImageLoading(false);
        setShowSkeleton(false);
      }
    } else {
      setImageLoading(false);
      setShowSkeleton(false);
    }
  }, [state.employee?.profile_photo]);

  const handleImageLoad = () => {
    setImageLoading(false);
    setImageError(false);
    setTimeout(() => setShowSkeleton(false), 100);
  };

  const handleImageError = () => {
    setImageLoading(false);
    setImageError(true);
    setShowSkeleton(false);
  };

  const getHeaderName = () => {
    const e = state.employee;
    return (
      e?.full_name || `${e?.first_name || ""} ${e?.last_name || ""}`.trim()
    );
  };

  const getHeaderEmail = () => state.employee?.email || "";

  const getHeaderImageSrc = () =>
    getEmployeeProfileImageUrl(state.employee?.profile_photo);

  const getPreviewImageSrc = () =>
    getEmployeeLargeImageUrl(state.employee?.profile_photo) ||
    getHeaderImageSrc();

  const handleRequestAction = (t: EmployeeConfirmType) => {
    setConfirmType(t);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (confirmLoading) return;
    setConfirmOpen(false);
    setConfirmType(null);
  };

  const handleConfirm = async () => {
    if (!confirmType || !state.employee?._id) return;

    const cfg = getConfirmConfig(confirmType);

    setConfirmLoading(true);
    try {
      const res = await AgentEmployeeApi.update(state.employee._id, {
        status: cfg.nextStatus,
      });

      if (res.success && res.data?.success) {
        toast.success("Changes saved successfully");

        setTimeout(() => {
          window.location.reload();
        }, 500);
      } else {
        toast.error(res?.error || res?.data?.message || "Failed to update");
        setConfirmLoading(false);
      }
    } catch (e: any) {
      toast.error(e?.message || "Failed to update");
      setConfirmLoading(false);
    }
  };

  const renderProfileImage = () => {
    const src = getHeaderImageSrc();

    if (imageError || !src) {
      return (
        <div className="default-avatar">
          {(getHeaderName() || "A")[0]?.toUpperCase()}
        </div>
      );
    }

    return (
      <>
        {showSkeleton && <div className="image-skeleton" />}
        <img
          ref={imageRef}
          src={src}
          alt={getHeaderName() || "Employee"}
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{
            opacity: imageLoading ? 0 : 1,
            transition: "opacity 0.3s ease-in-out",
          }}
        />
      </>
    );
  };

  const getDetails = () => {
    switch (type) {
      case "info":
        return (
          <AgentEmployeeInfoView
            employee={state.employee}
            onRefresh={fetchEmployee}
          />
        );
      case "permissions":
        return <div className="coming-soon">Permissions (coming soon)</div>;
      case "logs":
        return <div className="coming-soon">Logs (coming soon)</div>;
      default:
        return null;
    }
  };

  const canOpenPreview = !imageError && !!getHeaderImageSrc();

  return (
    <ProtectedComponent>
      <NavigationLayout>
        <div className="agency-employee-details-container">
          <div
            className="propertie-view-back-btn"
            onClick={() => navigate(clientRoutes.agentEmployeeList)}
          >
            <div className="back-icon">
              <BackArrowIcon />
            </div>
            <p className="bakc-text-contain">Back to list</p>
          </div>

          <div className="details-heading">
            <p>Agent employee details</p>

            {state.employee && (
              <AgencyEmployeeStatusMenu
                status={state.employee?.status || ""}
                onRequestAction={handleRequestAction}
              />
            )}
          </div>

          {state.isLoading ? (
            <div className="details-loader">
              <Loading />
            </div>
          ) : !state.employee ? (
            <div className="details-not-found">Employee not found</div>
          ) : (
            <>
              <div className="details-heading-name">
                <div
                  className="details-profile"
                  onClick={() => {
                    if (canOpenPreview) setIsPreviewOpen(true);
                  }}
                  style={{
                    cursor: canOpenPreview ? "pointer" : "default",
                  }}
                >
                  {renderProfileImage()}
                </div>

                <div className="details-info">
                  <p className="name">{getHeaderName()}</p>
                  <p className="email">{getHeaderEmail()}</p>
                </div>
              </div>

              <AgentEmployeeTabsView />
              {getDetails()}
            </>
          )}
        </div>

        <Modal open={isPreviewOpen} onClose={() => setIsPreviewOpen(false)}>
          <div className="employee-image-preview-modal">
            <button
              className="employee-image-preview-close"
              onClick={() => setIsPreviewOpen(false)}
            >
              <CloseIcon />
            </button>

            <img
              className="employee-image-preview-img"
              src={getPreviewImageSrc()}
              alt="preview"
              draggable={false}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).src =
                  "/images/profile_placeholder.png";
              }}
            />
          </div>
        </Modal>

        <Modal
          open={confirmOpen}
          onClose={closeConfirm}
          className="employee-confirm-modal"
          disableEscapeKeyDown={confirmLoading}
        >
          <div className="ecm-center" onClick={(e) => e.stopPropagation()}>
            <div className="ecm-card">
              {confirmType && (
                <>
                  {(() => {
                    const cfg = getConfirmConfig(confirmType);

                    return (
                      <>
                        <p className="ecm-title">{cfg.title}</p>

                        <div className="ecm-body">
                          {cfg.lines.map((t, idx) => (
                            <p key={idx} className="ecm-text">
                              {t}
                            </p>
                          ))}

                          {cfg.note ? (
                            <div className="ecm-note">
                              <p className="ecm-note-title">Note:</p>
                              <p className="ecm-note-text">{cfg.note}</p>
                            </div>
                          ) : null}
                        </div>

                        <div className="ecm-actions">
                          <button
                            className="ecm-cancel"
                            onClick={closeConfirm}
                            disabled={confirmLoading}
                            type="button"
                          >
                            Cancel
                          </button>

                          <button
                            className={`ecm-confirm ${
                              cfg.confirmVariant === "danger" ? "danger" : ""
                            }`}
                            onClick={handleConfirm}
                            disabled={confirmLoading}
                            type="button"
                          >
                            {confirmLoading ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              cfg.confirmText
                            )}
                          </button>
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        </Modal>
      </NavigationLayout>
    </ProtectedComponent>
  );
};

export default AgentEmployeeDetails;
